var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
import { InjectionToken, ModuleWithProviders, } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { catchError, filter, map, switchMap, tap, withLatestFrom, } from 'rxjs/operators';
import { ApiHttp } from './api-http.service';
import { assertNever, isDefined } from './helpers';
import { pluckItemFromRawStore } from './store.helpers';
var StoreBackend = /** @class */ (function () {
    function StoreBackend(store, apiHttp) {
        this.store = store;
        this.apiHttp = apiHttp;
        this.backendConfigs = {};
    }
    StoreBackend.prototype.fetch = function (ref) {
        var _this = this;
        var path = ref.path, query = ref.query;
        var type = path.collection;
        return Observable.of(this.backendConfigs[type].fetch).pipe(filter(isDefined), map(function (fetch) { return fetch(path.authUid, path.ids, query); }), switchMap(function (request) { return _this.apiHttp.fetch(request); }));
    };
    StoreBackend.prototype.search = function (key, query) {
        var _this = this;
        return query.pipe(map(function (q) { return _this.backendConfigs[key].search(q); }), switchMap(function (request) { return _this.apiHttp.fetch(request); }), map(function (response) {
            return (response.status === 'success'
                ? response.result
                : []);
        }));
    };
    StoreBackend.prototype.push = function (ref, object, extra) {
        var _this = this;
        var path = ref.path, query = ref.query;
        var type = path.collection;
        return Observable.of(this.backendConfigs[type].push).pipe(filter(isDefined), map(function (push) {
            var request = push(path.authUid, object, extra);
            return {
                request: request,
                payload: {
                    type: type,
                    ref: ref,
                    object: object,
                    rawRequest: request.payload,
                },
            };
        }), tap(function (_a) {
            var payload = _a.payload;
            var action = { type: "API_PUSH", payload: payload };
            _this.store.dispatch(action);
        }), switchMap(function (_a) {
            var request = _a.request;
            return _this.apiHttp.post(request);
        }), catchError(function (_a) {
            var result = _a.result, payload = _a.payload;
            _this.sendActions({ type: 'PUSH', payload: payload }, path, query, result);
            return Observable.throw(result);
        }), map(function (_a) {
            var result = _a.result, payload = _a.payload;
            return _this.sendActions({ type: 'PUSH', payload: payload }, path, query, result);
        }));
    };
    StoreBackend.prototype.set = function (ref, id, object) {
        var _this = this;
        var path = ref.path, query = ref.query;
        var type = path.collection;
        return Observable.of(this.backendConfigs[type].set).pipe(filter(isDefined), withLatestFrom(this.store), map(function (_a) {
            var set = _a[0], storeState = _a[1];
            var originalObject = pluckItemFromRawStore(storeState, path, id);
            if (originalObject === undefined) {
                throw new Error('Missing original object');
            }
            var request = set(path.authUid, object);
            return {
                request: request,
                payload: {
                    type: type,
                    ref: ref,
                    object: object,
                    rawRequest: request.payload,
                    originalObject: originalObject,
                },
            };
        }), tap(function (_a) {
            var payload = _a.payload;
            var action = { type: "API_SET", payload: payload };
            _this.store.dispatch(action);
        }), switchMap(function (_a) {
            var request = _a.request, payload = _a.payload;
            return _this.apiHttp.post(request);
        }, function (_a, result) {
            var payload = _a.payload;
            return ({ payload: payload, result: result });
        }), map(function (_a) {
            var payload = _a.payload, result = _a.result;
            return _this.sendActions({ type: 'SET', payload: payload }, path, query, result);
        }));
    };
    StoreBackend.prototype.update = function (ref, id, object) {
        var _this = this;
        var path = ref.path;
        var type = path.collection;
        return Observable.of(this.backendConfigs[type].update).pipe(filter(isDefined), withLatestFrom(this.store), map(function (_a) {
            var update = _a[0], storeState = _a[1];
            var originalObject = pluckItemFromRawStore(storeState, path, id);
            if (originalObject === undefined) {
                throw new Error('Missing original object');
            }
            var request = update(path.authUid, object, originalObject);
            return {
                request: request,
                payload: {
                    type: type,
                    ref: ref,
                    object: object,
                    rawRequest: request.payload,
                    originalObject: originalObject,
                },
            };
        }), tap(function (_a) {
            var payload = _a.payload;
            var action = { type: "API_UPDATE", payload: payload };
            _this.store.dispatch(action);
        }), switchMap(function (_a) {
            var request = _a.request, payload = _a.payload;
            switch (request.method) {
                case 'POST':
                    return _this.apiHttp.post(request);
                case 'PUT':
                    return _this.apiHttp.put(request);
                default:
                    return assertNever(request.method);
            }
        }, function (_a, result) {
            var payload = _a.payload;
            return ({ payload: payload, result: result });
        }), map(function (_a) {
            var payload = _a.payload, result = _a.result;
            return _this.sendActions({ type: 'UPDATE', payload: payload }, payload.ref.path, payload.ref.query, result);
        }));
    };
    StoreBackend.prototype.delete = function (ref, id) {
        var _this = this;
        var path = ref.path;
        var type = path.collection;
        return Observable.of(this.backendConfigs[type].remove).pipe(filter(isDefined), withLatestFrom(this.store), map(function (_a) {
            var del = _a[0], storeState = _a[1];
            var originalObject = pluckItemFromRawStore(storeState, path, id);
            if (originalObject === undefined) {
                throw new Error('Missing original object');
            }
            var request = del(path.authUid, id);
            return {
                request: request,
                payload: {
                    type: type,
                    ref: ref,
                    rawRequest: request.payload,
                    originalObject: originalObject,
                },
            };
        }), tap(function (_a) {
            var payload = _a.payload;
            var action = { type: "API_DELETE", payload: payload };
            _this.store.dispatch(action);
        }), switchMap(function (_a) {
            var request = _a.request, payload = _a.payload;
            switch (request.method) {
                case 'POST':
                    return _this.apiHttp.post(request);
                case 'PUT':
                    return _this.apiHttp.put(request);
                case 'DELETE':
                    return _this.apiHttp.delete(request);
                default:
                    return assertNever(request.method);
            }
        }, function (_a, result) {
            var payload = _a.payload;
            return ({ payload: payload, result: result });
        }), map(function (_a) {
            var payload = _a.payload, result = _a.result;
            return _this.sendActions({ type: 'DELETE', payload: payload }, payload.ref.path, payload.ref.query, result);
        }));
    };
    StoreBackend.prototype.sendActions = function (baseAction, path, query, data) {
        switch (data.status) {
            case 'success': {
                var action = baseAction.type === 'PUSH'
                    ? {
                        type: 'API_PUSH_SUCCESS',
                        payload: __assign({}, baseAction.payload, { result: data.result }),
                    }
                    : baseAction.type === 'SET'
                        ? {
                            type: 'API_SET_SUCCESS',
                            payload: __assign({}, baseAction.payload, { result: data.result }),
                        }
                        : baseAction.type === 'UPDATE'
                            ? {
                                type: 'API_UPDATE_SUCCESS',
                                payload: __assign({}, baseAction.payload, { result: data.result }),
                            }
                            : {
                                type: 'API_DELETE_SUCCESS',
                                payload: __assign({}, baseAction.payload, { result: data.result }),
                            };
                this.store.dispatch(action);
                return true;
            }
            case 'error': {
                var action = baseAction.type === 'PUSH'
                    ? {
                        type: 'API_PUSH_ERROR',
                        payload: __assign({}, baseAction.payload, { message: data.message }),
                    }
                    : baseAction.type === 'SET'
                        ? {
                            type: 'API_SET_ERROR',
                            payload: __assign({}, baseAction.payload, { message: data.message }),
                        }
                        : baseAction.type === 'UPDATE'
                            ? {
                                type: 'API_UPDATE_ERROR',
                                payload: __assign({}, baseAction.payload, { message: data.message }),
                            }
                            : {
                                type: 'API_DELETE_ERROR',
                                payload: __assign({}, baseAction.payload, { message: data.message }),
                            };
                this.store.dispatch(action);
                return false;
            }
            default: {
                var action = baseAction.type === 'PUSH'
                    ? {
                        type: 'API_PUSH_ERROR',
                        payload: __assign({}, baseAction.payload, { message: 'Unexpected API response', error: data }),
                    }
                    : baseAction.type === 'SET'
                        ? {
                            type: 'API_SET_ERROR',
                            payload: __assign({}, baseAction.payload, { message: 'Unexpected API response' }),
                        }
                        : baseAction.type === 'UPDATE'
                            ? {
                                type: 'API_UPDATE_ERROR',
                                payload: __assign({}, baseAction.payload, { message: 'Unexpected API response' }),
                            }
                            : {
                                type: 'API_DELETE_ERROR',
                                payload: __assign({}, baseAction.payload, { message: 'Unexpected API response' }),
                            };
                this.store.dispatch(action);
                return false;
            }
        }
    };
    StoreBackend.prototype.addFeature = function (collectionName, requestFactory) {
        this.backendConfigs[collectionName] = requestFactory;
    };
    return StoreBackend;
}());
export { StoreBackend };
export var BACKEND_COLLECTIONS = new InjectionToken('Backend Collections');
export var BACKEND_CONFIGS = new InjectionToken('Backend Configs');
var BackendRootModule = /** @class */ (function () {
    function BackendRootModule() {
    }
    return BackendRootModule;
}());
export { BackendRootModule };
var BackendFeatureModule = /** @class */ (function () {
    function BackendFeatureModule(storeBackend, backendCollections, backendConfigs) {
        backendCollections.map(function (collectionName, index) {
            storeBackend.addFeature(collectionName, backendConfigs[index]); // FIXME: any type
        });
    }
    return BackendFeatureModule;
}());
export { BackendFeatureModule };
var BackendModule = /** @class */ (function () {
    function BackendModule() {
    }
    BackendModule.forRoot = function () {
        return {
            ngModule: BackendRootModule,
            providers: [StoreBackend],
        };
    };
    BackendModule.forFeature = function (collectionName, configFactory) {
        return {
            ngModule: BackendFeatureModule,
            providers: [
                {
                    provide: BACKEND_COLLECTIONS,
                    multi: true,
                    useValue: collectionName,
                },
                {
                    provide: BACKEND_CONFIGS,
                    multi: true,
                    useFactory: configFactory,
                },
            ],
        };
    };
    return BackendModule;
}());
export { BackendModule };



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/core/backend.ts.pre-build-optimizer.js